import {useEffect, useCallback, useRef} from "react"
import {useAppToast} from "@ui-seeker/app"
import {getSubmit} from "@ui-seeker/utils"

import {BACKEND_APIS} from "@constants"
import {
    selectedBatchForItems,
    batchItemsData,
    batchItemsPagination,
    batchItemsMethodFilter,
    batchItemsResultFilter,
    batchItemsSearch,
    isLoadingBatchItems,
    batchItemsSummary
} from "../@signals"


export default function useBatchItems() {
    const {showToast} = useAppToast()
    const lastBatchIdRef = useRef(null)

    const fetchItems = useCallback((options = {}) => {
        const selectedBatch = selectedBatchForItems.value
        if (!selectedBatch?.batchID) return

        const {
            page = batchItemsPagination.value.page,
            pageSize = batchItemsPagination.value.pageSize,
            methodFilter = batchItemsMethodFilter.value,
            resultFilter = batchItemsResultFilter.value
        } = options

        isLoadingBatchItems.value = true

        const params = {
            page,
            pageSize,
            sortBy: 'ts',
            sortOrder: 'desc'
        }

        if (methodFilter && methodFilter !== 'all') {
            params.method = methodFilter
        }

        if (resultFilter && resultFilter !== 'all') {
            params.result = resultFilter
        }

        getSubmit(
            `${BACKEND_APIS.BATCH_ADMIN}/admin/batches/${selectedBatch.batchID}/items`,
            params
        ).then(res => {
            batchItemsData.value = res.items || []
            batchItemsPagination.value = res.pagination || {
                page: 1,
                pageSize: 50,
                totalItems: 0,
                totalPages: 0
            }
            batchItemsSummary.value = res.summary || null
        }).catch((err) => {
            showToast.error('Failed to load batch items')
            console.error('Error fetching batch items:', err)
        }).finally(() => {
            isLoadingBatchItems.value = false
        })
    }, [showToast])

    // Fetch when selected batch changes
    useEffect(() => {
        const batchId = selectedBatchForItems.value?.batchID

        if (batchId && batchId !== lastBatchIdRef.current) {
            lastBatchIdRef.current = batchId
            // Reset state when batch changes
            batchItemsData.value = []
            batchItemsPagination.value = {
                page: 1,
                pageSize: 50,
                totalItems: 0,
                totalPages: 0
            }
            batchItemsMethodFilter.value = 'all'
            batchItemsResultFilter.value = 'all'
            batchItemsSearch.value = ''
            batchItemsSummary.value = null
            fetchItems({page: 1, methodFilter: 'all', resultFilter: 'all'})
        } else if (!batchId) {
            lastBatchIdRef.current = null
        }
    }, [selectedBatchForItems.value?.batchID, fetchItems])

    const setPage = useCallback((newPage) => {
        fetchItems({page: newPage})
    }, [fetchItems])

    const setPageSize = useCallback((newPageSize) => {
        fetchItems({page: 1, pageSize: newPageSize})
    }, [fetchItems])

    const setMethodFilter = useCallback((newFilter) => {
        batchItemsMethodFilter.value = newFilter
        fetchItems({page: 1, methodFilter: newFilter})
    }, [fetchItems])

    const setResultFilter = useCallback((newFilter) => {
        batchItemsResultFilter.value = newFilter
        fetchItems({page: 1, resultFilter: newFilter})
    }, [fetchItems])

    // Free-text search: filtering is client-side, so we must have all items loaded.
    // When a term is entered, load the whole batch (for the current chip filters);
    // when cleared, restore normal server pagination.
    const setSearch = useCallback((term) => {
        batchItemsSearch.value = term
        if (term && term.trim()) {
            const total = batchItemsPagination.value.totalItems || 0
            fetchItems({page: 1, pageSize: total > 0 ? total : 10000})
        } else {
            fetchItems({page: 1, pageSize: 50})
        }
    }, [fetchItems])

    const closeItemsView = useCallback(() => {
        lastBatchIdRef.current = null
        selectedBatchForItems.value = null
        batchItemsData.value = []
        batchItemsPagination.value = {
            page: 1,
            pageSize: 50,
            totalItems: 0,
            totalPages: 0
        }
        batchItemsMethodFilter.value = 'all'
        batchItemsResultFilter.value = 'all'
        batchItemsSearch.value = ''
        batchItemsSummary.value = null
    }, [])

    return {
        items: batchItemsData.value,
        pagination: batchItemsPagination.value,
        summary: batchItemsSummary.value,
        methodFilter: batchItemsMethodFilter.value,
        resultFilter: batchItemsResultFilter.value,
        search: batchItemsSearch.value,
        isLoading: isLoadingBatchItems.value,
        selectedBatch: selectedBatchForItems.value,
        fetchItems,
        setPage,
        setPageSize,
        setMethodFilter,
        setResultFilter,
        setSearch,
        closeItemsView
    }
}
